<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { useSessionStore } from '@/store/sessionStore';
import { useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import axiosInstance from '@/plugins/axios';

const sessionStore = useSessionStore();
const router = useRouter();
interface Translation {
    _id: string;
    languageIdentifier: string;
    languageCode: string;
    description?: string;
}

const translations = ref<Translation[]>([]);
const tuevLanguages = ref(['ailab-general-de', 'ailab-general-en']);
const allowedRoles = ref(['ai_lab_admin', 'ai_lab_user']);

const tenant = computed(() => sessionStore.getTenant() || {
    name: '',
    domains: [],
    settings: {
        enabledLanguages: [],
        defaultLanguage: ''
    }
});

const user = computed(() => sessionStore.getUser() || {
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    availableLanguages: []
});

const goTo = (lang: string) => {
    router.push({ path: `/tenant/translations/${lang._id}` });
};


const getTranslations = async () => {
    try {
        const loadedTranslations: any[] = [];

        for (const lang of tenant.value.adminSettings.enabledLanguages) {
            const response = await axiosInstance.get(`/translations/${lang}`);
            loadedTranslations.push(response.data);
        }

        translations.value = loadedTranslations.sort((a, b) => {
            const priority = ['ailab-general-de', 'ailab-general-en'];

            const aIndex = priority.indexOf(a.languageIdentifier);
            const bIndex = priority.indexOf(b.languageIdentifier);

            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;

            return a.languageIdentifier.localeCompare(b.languageIdentifier);
        });
    } catch (error) {
        console.log(error);
    }
};


const checkLanguagePermission = (lang: string) => {
    if (tuevLanguages.value.includes(lang.languageIdentifier) && !allowedRoles.value.includes(user.value.role)) {
        return false;
    }
    if (user.value.availableLanguages && user.value.availableLanguages.length > 0) {
        if (!user.value.availableLanguages.includes(lang._id)) {
            return false;
        }
    }
    return true;
}

onMounted(async () => {
    await getTranslations();
});


const languageMap: Record<string, string> = {
    de: 'German',
    en: 'English',
    fr: 'French',
    it: 'Italian',
    es: 'Spanish',
    pt: 'Portuguese',
    nl: 'Dutch',
    da: 'Danish',
    fi: 'Finnish',
    no: 'Norwegian',
};


const formatLanguageIdentifier = (identifier: string): string => {
  const parts = identifier.split('-');
  const langCode = parts.pop();
  const langName = languageMap[langCode || ''] || langCode?.toUpperCase() || '';


  return `${langName}`.trim();

};

</script>


<template>
    <div>
    <div class="col-12">
        <div class="card-grid">
            <Card :class="{ 'menuCard': true, 'disabled-card': !checkLanguagePermission(lang) }" label="Edit"
                v-for="lang in translations" v-bind:key="lang"
                @click="checkLanguagePermission(lang) ? goTo(lang) : null">
                <template #title>
                    {{ formatLanguageIdentifier(lang.languageIdentifier) }}
                </template>
                <template #subtitle>
                    {{ lang.languageIdentifier }}
                </template>
                <template #content>
                    <ProgressBar class="translationProgressBar" :value="lang.progress.percentRequired" :max="100">
                    </ProgressBar>
                    <span v-if="lang.languageCode === 'en'" class="flag-icon fi fi-gb"></span>
                    <span v-else :class="`fi fi-${lang.languageCode}`" class="flag-icon"></span>
                </template>
            </Card>
        </div>
    </div>
    </div>
</template>

<style lang="css" scoped>

</style>